import {each, assert, uniform1, DEG2RAD, range, chance, choice, expovariate, uniform, randint, lerp, shuffle, qs} from './utils';
import {mat3, mat4, quat, vec3, vec2, vec4} from 'gl-matrix';
import {use_program, load_texture, create_texture, RenderTarget} from './shader-utils';
import {create_buffer} from './webgl';
import {add_hotkeys} from './hotkeys';
import {simplex2} from './noise';
import {MusicPlayer} from './music-player';

const media_base = 'media/tiles';

export function init_gallery(env) {
    const default_volume = 0.14;

    env.params.add_float('master_volume', {min:0, max:1, default: default_volume});
    env.params.add_float('filter_mix', {min:0, max:1, default: 0.5});

    const buf_tri = create_buffer(gl.ARRAY_BUFFER, [-1, -1, 3, -1, -1, 3]);
    const rt = new RenderTarget;

    const texture_size = [1024, 1024];
    const texture = create_texture({
        size: texture_size,
        filter: gl.LINEAR,
    });

    {
        rt.bind(texture, 0);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        rt.unbind();
    }

    const media = {
        videos: [],
        images: [],
        audios: [],
    };

    function rotate(arr) {
        const x = arr.shift();
        arr.push(x);
        return x;
    }

    //fetch('/api/media/')
    fetch(`${media_base}/index.json`)
        .then(r => r.json())
        .then(items => {
            let skipped = 0;

            items.forEach(item => {
                const tags = item.tags || [];

                if (tags.includes('ay')) {
                    // skip
                    ++skipped;
                    return;
                }

                item.hero = tags.includes('blm');

                if (item.type == 'video')
                    media.videos.push(item);
                else if (item.type == 'image')
                    media.images.push(item);
                else if (item.type == 'audio')
                    media.audios.push(item);
            });

            console.log(`got media: ${media.videos.length} videos, ${media.images.length} images, ${skipped} skipped, ${media.audios.length} audios`);

            for (let items of Object.values(media)) {
                shuffle(items);

                const hero_items = items.filter(i => i.hero);

                if (!hero_items.length)
                    continue;

                for (let idx = 0; idx < items.length;) {
                    items.splice(idx, 0, choice(hero_items));
                    idx += randint(7, 10);
                }
            }
        });

    // the sole media element
    const video = document.createElement('video');
    video.playsInline = true;
    video.loop = false;
    video.src = 'images/empty.mp4';
    video.load();
    video.volume = default_volume;
    //video.muted = true;

    if (0) {
        // video event debug
        const event_names = [
            'canplay',
            'canplaythrough',
            'durationchange',
            'emptied',
            'ended',
            'loadeddata',
            'loadedmetadata',
            'pause',
            'play',
            'playing',
            'progress',
            'ratechange',
            'seeked',
            'seeking',
            'stalled',
            'suspend',
            'timeupdate',
            'volumechange',
            'waiting',
        ];

        console.log('video added events');
        each(event_names, name => video.addEventListener(name, e => {
            console.log('video event:', name);
        }));
    }

    let video_enabled = false;
    let video_playing = false;
    let video_errors = 0;

    {
        // gate texture upload to when video is playing

        video.onplaying = function() {
            video_playing = true;
        };

        video.onended = function() {
            video_playing = false;
        };
    }

    let webcam_enabled = false;
    let webcam_texture = null;
    let webcam_start_time = 0;
    const webcam_max_duration = 5e3;

    let audio_enabled = false;

    // const audio = document.createElement('audio');
    // audio.src = 'media/sample.ogg';
    // audio.loop = true;
    // audio.play();
    //

    {
        // may want to look here...
        // https://www.mattmontag.com/web/unlock-web-audio-in-safari-for-ios-and-macos
        // can i use audio_ctx.state with touchstart?

        let tap_start_time = 0;
        let tap_moved = false;

        function on_tap_start(e) {
            tap_start_time = e.timeStamp;
            tap_moved = false;
        }

        function on_tap_move(e) {
            tap_moved = true;
        }

        function on_tap_end(e) {
            const tap_duration = e.timeStamp - tap_start_time;
            console.log('tap duration:', ~~tap_duration);
            
            if (tap_duration >= 700) {
                console.log('tap too long');
                return;
            }

            if (tap_moved) {
                console.log('tap moved');
                return;
            }

            enable_video_and_audio();
        }

        function enable_video_and_audio() {
            remove_event_listeners();
            console.log('enabling video & audio...');
            enable_video();
            enable_audio();
        }

        function on_mouse_up() {
            enable_video_and_audio();
        }

        function add_event_listeners() {
            document.addEventListener('touchstart', on_tap_start, {passive:true});
            document.addEventListener('touchmove', on_tap_move);
            document.addEventListener('touchend', on_tap_end);
            document.addEventListener('mouseup', on_mouse_up);
        }

        function remove_event_listeners() {
            document.removeEventListener('touchstart', on_tap_start);
            document.removeEventListener('touchmove', on_tap_move);
            document.removeEventListener('touchend', on_tap_end);
            document.removeEventListener('mouseup', on_mouse_up);
        }

        add_event_listeners();
    }

    function enable_video() {
        if (video_enabled) {
            // noop
            return;
        }

        video.play()
            .then(() => {
                video_enabled = true;
                console.log('video_enabled:', video_enabled);
            })
            .catch(err => {
                console.error('video enable failed:', err);
            });
    }

    let video_slot_idx = -1;

    function next_music_url() {
        const item = rotate(media.audios);
        return `media/tiles/${item.uid}.m4a`;
    }

    const music_player = new MusicPlayer(next_music_url);
    music_player.set_volume(default_volume);

    function enable_audio() {
        if (audio_enabled)
            return;

        audio_enabled = true;
        music_player.start();
        init_sounds();
        console.log('audio enabled');
    }

    function init_sounds() {
        function load_sound(name) {
            const a = document.createElement('audio');
            a.src = `sounds/${name}.mp4`;
            a.load();
            return a;
        }

        const sounds = {
            explode: load_sound('explode'),
            webcam: load_sound('webcam'),
        };

        function play(name, volume=1) {
            if (!audio_enabled) {
                console.warn('play_sound: audio not enabled yet');
                return;
            }

            const sound = sounds[name];

            if (!sound) {
                console.warn('sounds: unknown:', name);
                return;
            }

            sound.volume = volume;
            sound.play();
        }

        env.play_sound = play;
    }

    function Slot(id) {
        this.id = id;
        this.seqnum = 0;
        this.aspect = 1.0;
        this.expiry = 0;
        this.opacity = 0;
        this.is_video = false;
    }

    const slots = range(3).map(idx => new Slot(idx + 1));
    const mean_image_duration = 5000;

    function update_slots(env) {
        const now = env.time;

        slots.forEach((slot, slot_idx) => {
            if (now < slot.expiry) {
                const t = slot.expiry - now;
                const fade_time_ms = 1500;
                if (t < fade_time_ms) {
                    slot.opacity = t/fade_time_ms;
                    // if (slot_idx == 0) console.log('fade:', slot.opacity);
                } else {
                    slot.opacity = Math.min(1, slot.opacity + .01);
                }
                return;
            }

            // expired slot... need to load something else
            let is_webcam_slot = (webcam_enabled && slot_idx === video_slot_idx);
            if (!is_webcam_slot && chance(0.1)) {
                // clear the slot
                ++slot.seqnum;
                slot.aspect = 1;
                slot.expiry = now + uniform(3, 8)*1e3;
                slot.opacity = 0;
                slot.is_video = 0;
                if (video_slot_idx === slot_idx)
                    video_slot_idx = -1;

                // clear the slot
                {
                    const slot = slot_idx + 1;
                    const level = 9;
                    const x = (slot & 1) << level;
                    const y = ((slot & 2) >> 1) << level;
                    const size = 1 << level;

                    rt.bind(texture, 0);
                    gl.enable(gl.SCISSOR_TEST);
                    gl.clearColor(0, 0, 0, 0);

                    gl.scissor(x, y, size, size);
                    gl.clear(gl.COLOR_BUFFER_BIT);

                    gl.scissor(x>>1, y>>1, size>>1, size>>1);
                    gl.clear(gl.COLOR_BUFFER_BIT);

                    gl.disable(gl.SCISSOR_TEST);
                    rt.unbind();
                }
                return;
            }

            // if (is_webcam_slot) {
            //     video.srcObject = null;
            //     webcam_enabled = false;
            //     webcam_texture = null;
            //     video_slot_idx = -1;
            // }
            //

            const can_load_video = video_enabled && (video_slot_idx < 0);
            const items = (can_load_video && !webcam_enabled) ?
                media.videos :
                media.images;

            if (!items.length)
                return;

            const item = rotate(items);

            if (item.type == 'video') {
                load_and_place(item.uid, slot.id, 8);
                load_video(item);
                video_slot_idx = slot_idx;
                slot.expiry = now + item.duration*1e3;
                slot.opacity = 0;
            } else {
                load_and_place(item.uid, slot.id, 9);
                load_and_place(item.uid, slot.id, 8);
                if (video_slot_idx === slot_idx)
                    video_slot_idx = -1;
                slot.expiry = now + uniform(3, 8)*1e3;
                slot.opacity = 0;
            }

            slot.aspect = item.aspect;
            slot.is_video = (video_slot_idx === slot_idx);
            ++slot.seqnum;
            //expovariate(mean_image_duration);
        });

        if (video_slot_idx >= 0) {
            // upload texture
            const slot = video_slot_idx + 1;
            const level = 9;
            const x = (slot & 1) << level;
            const y = ((slot & 2) >> 1) << level;

            const size = 1 << level;
            //if (video.videoWidth === size && video.videoHeight === size)
            if (webcam_enabled) {
                if (webcam_texture && video_playing) {
                    gl.bindTexture(gl.TEXTURE_2D, webcam_texture);
                    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
                    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, video);

                    // draw into slot
                    const vp_save = gl.getParameter(gl.VIEWPORT);

                    {
                        rt.bind(texture, 0);
                        const pgm = use_program('blit');
                        if (pgm) {
                            gl.disable(gl.DEPTH_TEST);

                            gl.bindBuffer(gl.ARRAY_BUFFER, buf_tri);
                            pgm.vertexAttribPointer('aCoord', 2, gl.FLOAT, false, 0, 0);
                            pgm.uniformSampler2D('uTexColor', webcam_texture);

                            gl.viewport(x, y, size, size);
                            gl.drawArrays(gl.TRIANGLES, 0, 3);

                            // "blurred" version
                            gl.viewport(x>>1, y>>1, size>>1, size>>1);
                            gl.drawArrays(gl.TRIANGLES, 0, 3);
                        }

                        rt.unbind();
                    }

                    gl.viewport(...vp_save);
                }
            } else {
                if (video_playing) {
                    gl.bindTexture(gl.TEXTURE_2D, texture);
                    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
                    gl.texSubImage2D(gl.TEXTURE_2D, 0, x, y, gl.RGBA, gl.UNSIGNED_BYTE, video);
                }
            }
        }
    }

    function tile_url(uid, level, ext) {
        return `${media_base}/${uid}_${level}.${ext}`;
    }

    function load_and_place(uid, slot, level) {
        const image = new Image;
        image.src = tile_url(uid, (level == 8) ? '8b' : level, 'jpg');
        image.onload = function() {
            const x = (slot & 1) << level;
            const y = ((slot & 2) >> 1) << level;
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texSubImage2D(gl.TEXTURE_2D, 0, x, y, gl.RGBA, gl.UNSIGNED_BYTE, image);
        };
    }

    function expire_video_slot() {
        // if there's a current video slot, make it out of date
        if (video_slot_idx < 0)
            return;

        slots[video_slot_idx].expiry = env.time;
        video_slot_idx = -1;
    }
    
    async function load_video(item) {
        // about to change src: stop uploading
        video_playing = false;

        video.src = tile_url(item.uid, 9, 'mp4');
        video.load();

        // whether to play silent or not
        video.volume = (item.hero || chance(0.5)) ? default_volume : 0.0;

        try {
            //console.log('video.play()');
            await video.play()
            console.log('video: playing', item.uid);
            //console.log('- play success');
            //init_audio_context();

            // XXX should i do something here with musicplayer?
        }
        catch (err) {
            {
                ++video_errors;
                //qs('.debug').innerHTML = `video errors: ${video_errors}`;
            }

            console.error('video play error:', err);
            expire_video_slot();
        }
    }

    async function enable_webcam() {
        if (webcam_enabled)
            return;

        try {
            // pick a slot
            webcam_enabled = true;
            webcam_start_time = env.time;

            //const constraints = {video: {width: 512, height: 512}, audio: false};
            const constraints = {video: true, audio: false};
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            //console.log('webcam stream:', stream);

            video_slot_idx = 0;
            const slot = slots[video_slot_idx];
            ++slot.seqnum;
            slot.expiry = env.time + 864e5;
            //slot.expiry = env.time + 5e3;
            slot.opacity = 0;
            slot.is_video = true;

            video.onloadedmetadata = function() {
                if (!webcam_enabled)
                    return;

                console.log('webcam video:', video.videoWidth, video.videoHeight);
                webcam_texture = create_texture({
                    filter: gl.LINEAR,
                    storage: false,
                });
                gl.bindTexture(gl.TEXTURE_2D, webcam_texture);
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
                slot.aspect = video.videoWidth / video.videoHeight;

                video_playing = true;

                env.api_state_change({isWebcamEnabled: true});
            };

            // stop uploading
            video_playing = false;

            video.srcObject = stream;
            video.play(); // XXX do i need to await??

            env.play_sound('webcam', 1, 100 * uniform1());
        } catch (err) {
            webcam_enabled = false;
            webcam_texture = null;
            console.error('webcam error:', err);
            update_webcam_toggle();
            return;
        }
    }

    function disable_webcam() {
        if (webcam_enabled) {
            video.srcObject = null;
            webcam_enabled = false;
            webcam_texture = null;
            expire_video_slot();
            env.api_state_change({isWebcamEnabled: false});
        }
    }

    function toggle_webcam(enabled) {
        if (typeof enabled === 'undefined') {
            webcam_enabled ? disable_webcam() : enable_webcam();
        } else {
            enabled ? enable_webcam() : disable_webcam();
        }

        update_webcam_toggle();
    }

    function update_webcam_toggle() {
        const el = qs('.btn-webcam');
        if (el)
            el.classList.toggle('active', webcam_enabled);
    }

    function toggle_debug_texture() {
        env.toggle_debug_texture({
            tex: texture,
            size: texture_size,
        });
    }

    //toggle_debug_texture();

    // qs('.btn-webcam').onclick = function(e) {
    //     video_enabled = true;
    //     toggle_webcam();
    //     e.preventDefault();
    // };
    
    function toggle_mute() {
        let was_muted = video.muted;
        video.muted = !was_muted;
        music_player.set_volume(was_muted ? default_volume : 0);
        env.api_state_change({isMuted: !was_muted});
    }

    function toggle_webcam() {
        if (webcam_enabled) {
            disable_webcam();
        } else {
            enable_webcam();
        }
    }

    /*
    add_hotkeys(document, {
        KeyT: toggle_debug_texture,
        KeyM() {
            toggle_mute();
        },
        KeyW() {
            toggle_webcam();
        },
        KeyF() {
            randomize_filter();
        },
        KeyX() {
            console.log('KEY X');
            env.play_sound('explode', 1);
        }
    });
    */

    function check_webcam_duration() {
        if (!webcam_enabled)
            return;

        const now = env.time;
        const t = now - webcam_start_time;
        if (t > webcam_max_duration) {
            toggle_webcam(false);
        }
    }

    function draw(env) {
        check_webcam_duration();
        update_slots(env);
    }

    return {
        texture,
        slots,
        draw,
        toggle_mute,
        toggle_webcam,
        toggle_debug_texture,
    };
}
